import { useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import useAuth from "../../hooks/useAuth";
import AxiosHelper from "../../api/AxiosHelper";
import Constants from './../../utils/constants';
import Wrapper from "./Wrapper";
import InputPassword from "../../components/form/InputPassword";
import { useTranslation } from "react-i18next";
import FailedLogin from "./FailedLogin";

const Login = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { auth, setAuth, ready, setPanel, setToast } = useAuth();
    const usernameRef = useRef();
    
    const [username, setUsername] = useState(''); 
    const [password, setPassword] = useState('');

    const [failedCount, setFailedCount] = useState();
    const [countDown, setCountDown] = useState(null);

    useEffect(() => {
        if (ready && !auth.token) usernameRef.current.focus();
    }, [ready]);

    useEffect(() => {
        if (auth != 'Unauthorized' && ready)
            navigateToPage(auth.role);
    }, [auth]);

    // Keep in step with useRefreshToken: every branch stores a panel, so a value
    // left in localStorage by an earlier session on this browser cannot leak into
    // the next one and send useBasePath to the wrong prefix.
    const navigateToPage = (role) => {
        switch(role.type) {
            case Constants.ROOT:
            case Constants.ADMIN:
                setPanel(Constants.ADMIN);
                localStorage.setItem('panel', Constants.ADMIN);
                navigate("/");
                break;
            case Constants.CASHIER:
                setPanel(Constants.CASHIER);
                localStorage.setItem('panel', Constants.CASHIER);
                navigate("/cp");
                break;
            default:
                setPanel(Constants.ADMIN);
                localStorage.setItem('panel', Constants.ADMIN);
                navigate("/");
                break;
        }
    }

    const handleLoginSubmit = (event) => {
        event.preventDefault();

        const data = { username, password }
        AxiosHelper.login(data).then((res) => {
            if (res.status == 200) {
                const authData = res.data;
                setAuth(authData);
                navigateToPage(authData.role);
            }
        }).catch((err) => {
            if (!err?.response) {
                setToast({ content: t('NO_SERVER_RESPONSE'), variant: 'danger', title: t('ERROR'), show: true });
                return;
            }
            switch(err.response.status) {
                case 401: setToast({ content: t('NOT_VERIFIED'), variant: 'danger', title: t('ERROR'), show: true }); break;
                case 402:
                    setToast({ content: t('VALIDATION_ERROR'), variant: 'danger', title: t('FAILED_LOGIN'), show: true });
                    setFailedCount(err.response.data.failedCount);
                    break;
                case 404: setToast({ content: t('USER_NOT_FOUND'), variant: 'warning', title: t('NOT_FOUND'), show: true }); break;
                case 410:
                    setToast({ content: t('PLEASE_WAIT_THEN_TRY_AGAIN'), variant: 'danger', title: t('ERROR'), show: true });
                    break;
                case 411:
                    setToast({ content: t('TOO_MANY_FAILED_LOGIN_ATTEMPTS'), variant: 'danger', title: t('ERROR'), show: true });
                    setFailedCount(err.response.data.failedCount);
                    const endTimestamp = new Date().getTime() + 60 * 1000; // 60s
                    localStorage.setItem('timerEnd', new Date(endTimestamp));
                    setCountDown(60);
                    break;
                case 420: setToast({ content: t('EMAIL_OR_PWD_NOT_ENTERED'), variant: 'warning', title: t('WARNING'), show: true }); break;
                default : setToast({ content: err.response.data.msg, variant: 'danger', title: t('ERROR'), show: true }); break;
            }
        });
    }

    return (
        (!ready && auth.token == 'Unauthorized') &&
        <Wrapper>
            <div className="d-flex flex-column">
                <div className="d-flex flex-column mx-5">
                    <h1 className="fw-bold display-5">{t('SIGN_IN')}</h1>
                    <p className="text-muted mb-5">{t('ENTER_LOGIN_CREDENTIALS')}</p>
                </div>
                <form onSubmit={handleLoginSubmit} className="mx-5" noValidate>
                    <FailedLogin 
                        failedCount={failedCount}
                        setFailedCount={setFailedCount}
                        countDown={countDown}
                        setCountDown={setCountDown}
                    />
                    <input
                        type="text" ref={usernameRef} autoFocus={true}
                        className="form-control mb-3"
                        placeholder={t('USERNAME')}
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        disabled={countDown}
                    />
                    <InputPassword
                        className={'mt-3 mb-2'} inputClass={'py-2'}
                        value={password} setValue={setPassword}
                        hint={t('YOUR_PASSWORD')} required={true} disabled={countDown}
                    />
                    <div className="d-flex flex-row justify-content-between">
                        <span></span>
                        <button type="submit"
                            className="button-u col-md-4 bg-blue-600 bg-gradient text-white font-14 border border-info my-3 py-2 rounded-2"
                            disabled={(!username || !password || countDown)}>
                            {t('SIGN_IN')}
                        </button>
                    </div>
                </form>
            </div>
        </Wrapper>
    );
};

export default Login;